if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mqlMobile = window.matchMedia('(max-width: 1000px)');
        
        // Initialize mobile gallery swap feature
        this.initMobileGallerySwap();
        
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }
      
      initMobileGallerySwap() {
        if (!this.mqlMobile.matches) return;
        
        const mediaList = this.querySelector('.product__media-list.slider--mobile');
        if (!mediaList) return;
        
        const slides = mediaList.querySelectorAll('.slider__slide');
        if (slides.length < 2) return;
        
        const firstSlide = slides[0];
        const secondSlide = slides[1];
        let isTransitioning = false;
        
        // Override slider calculations to ignore absolutely positioned first slide
        const sliderComponent = this.elements.viewer;
        if (sliderComponent && sliderComponent.slider) {
          // Store original methods
          const originalInitPages = sliderComponent.initPages.bind(sliderComponent);
          const originalUpdate = sliderComponent.update.bind(sliderComponent);
          
          // Override initPages to exclude first slide from calculations
          sliderComponent.initPages = function() {
            this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
            
            // On mobile gallery, start offset calculation from second slide
            if (this.sliderItemsToShow.length < 2) return;
            
            // Use second and third slides for offset calculation (skip absolutely positioned first)
            const startIndex = 1;
            if (this.sliderItemsToShow.length > startIndex + 1) {
              this.sliderItemOffset = this.sliderItemsToShow[startIndex + 1].offsetLeft - this.sliderItemsToShow[startIndex].offsetLeft;
            } else if (this.sliderItemsToShow.length > startIndex) {
              this.sliderItemOffset = this.sliderItemsToShow[startIndex].clientWidth;
            }
            
            // Calculate slidesPerPage - on mobile it's typically 1
            this.slidesPerPage = 1;
            
            // Calculate total pages: all slides minus 1 (because first two merge)
            // 12 images - 1 merge = 11 pages
            this.totalPages = Math.max(1, this.sliderItemsToShow.length - 1);
            this.update();
          };
          
          // Override update to calculate page based on second slide position
          sliderComponent.update = function() {
            if (!this.slider || !this.nextButton) return;

            const previousPage = this.currentPage;
            
            // Calculate current page based on scroll position relative to second slide
            const secondSlideOffset = this.sliderItemsToShow[1]?.offsetLeft || 0;
            const relativeScroll = this.slider.scrollLeft - secondSlideOffset;
            
            // Page 1 = at second slide, Page 2 = one offset away, etc.
            if (relativeScroll < this.sliderItemOffset / 2) {
              this.currentPage = 1;
            } else {
              this.currentPage = Math.round(relativeScroll / this.sliderItemOffset) + 1;
            }
            
            this.currentPage = Math.max(1, Math.min(this.currentPage, this.totalPages));

            if (this.currentPageElement && this.pageTotalElement) {
              this.currentPageElement.textContent = this.currentPage;
              this.pageTotalElement.textContent = this.totalPages;
            }

            if (this.currentPage != previousPage) {
              this.dispatchEvent(
                new CustomEvent('slideChanged', {
                  detail: {
                    currentPage: this.currentPage,
                    currentElement: this.sliderItemsToShow[this.currentPage],
                  },
                })
              );
            }

            if (this.enableSliderLooping) return;

            // Check if at start (second slide visible at scroll position 0 or near 0)
            if (this.slider.scrollLeft <= secondSlideOffset + 10) {
              this.prevButton.setAttribute('disabled', 'disabled');
            } else {
              this.prevButton.removeAttribute('disabled');
            }

            if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
              this.nextButton.setAttribute('disabled', 'disabled');
            } else {
              this.nextButton.removeAttribute('disabled');
            }
          };
          
          // Re-initialize with new methods
          sliderComponent.initPages();
        }
        
        // Function to handle overlay click with fade transition
        const handleOverlayClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          
          // Prevent multiple clicks during transition
          if (isTransitioning) return false;
          isTransitioning = true;
          
          // Step 1: Add transitioning class to fade out the main image
          mediaList.classList.add('gallery-transitioning');
          
          // Step 2: After fade out, toggle swapped state
          setTimeout(() => {
            mediaList.classList.toggle('gallery-swapped');
            
            // Step 3: Remove transitioning class to fade in at new position
            setTimeout(() => {
              mediaList.classList.remove('gallery-transitioning');
              isTransitioning = false;
            }, 50); // Small delay to ensure position change happens first
          }, 250); // Match CSS transition duration (0.25s)
          
          return false;
        };
        
        // Add click event to first slide (overlay) - use capture phase to intercept early
        firstSlide.addEventListener('click', handleOverlayClick, true);
        
        // Prevent modal opener clicks on first slide
        const firstSlideModalOpener = firstSlide.querySelector('.product__modal-opener');
        if (firstSlideModalOpener) {
          firstSlideModalOpener.addEventListener('click', handleOverlayClick, true);
        }
        
        // Add click event to second slide when swapped
        secondSlide.addEventListener('click', (e) => {
          if (mediaList.classList.contains('gallery-swapped')) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            handleOverlayClick(e);
            return false;
          }
        }, true);
        
        // Prevent modal opener clicks on second slide when swapped
        const secondSlideModalOpener = secondSlide.querySelector('.product__modal-opener');
        if (secondSlideModalOpener) {
          secondSlideModalOpener.addEventListener('click', (e) => {
            if (mediaList.classList.contains('gallery-swapped')) {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              handleOverlayClick(e);
              return false;
            }
          }, true);
        }
      }

      onSlideChanged(event) {
        // Check if event.detail and event.detail.currentElement exist before accessing properties
        if (!event.detail || !event.detail.currentElement) return;
        
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
